import { ApiError } from "@/lib/api"

/**
 * Classification and retry bookkeeping for router-level load errors. Pure logic only —
 * the components in `components/errors/` render from these decisions.
 */

/** Fetch-rejection message prefixes: Chromium ("Failed to fetch"), WebKit ("Load failed"),
 * Firefox ("NetworkError when attempting to fetch resource."). `apiFetch` never wraps network
 * failures, so real connectivity errors always arrive as a TypeError with one of these.
 * A bare `instanceof TypeError` check would misclassify render bugs as connectivity loss. */
const NETWORK_MESSAGE_RE = /^(Failed to fetch|Load failed|NetworkError)/

/** True for errors worth auto-retrying: the API is unreachable or answering 502/503/504
 * (deploy window, proxy blip) — not for genuine application errors. */
export function isTransientRouteError(error: unknown): boolean {
	if (error instanceof ApiError) return error.status >= 502
	return error instanceof TypeError && NETWORK_MESSAGE_RE.test(error.message)
}

/** Print routes are rendered by the headless PDF worker, which waits on `body[data-print-ready]` —
 * they must never get retry loops or reloads (see the print branch in router-error-component). */
export function isPrintPathname(pathname: string): boolean {
	return pathname.startsWith("/print")
}

/** Routes an anonymous visitor can be on — don't offer dashboard links that bounce to sign-in. */
export function isPublicPathname(pathname: string): boolean {
	return pathname.startsWith("/share") || pathname.startsWith("/q/") || pathname.startsWith("/sign-in") || pathname.startsWith("/sign-up")
}

/** Report to Sentry only what ISN'T already reported: ApiError/network failures flow through
 * TanStack Query's cache-level onError (see query-client.ts) — capturing them again would
 * double-count. Render/programming errors never touch the QueryCache and would otherwise be
 * invisible (error boundaries swallow them from the global handler). */
export function shouldReportRouteError(error: unknown): boolean {
	return !(error instanceof ApiError) && !isTransientRouteError(error)
}

/** Auto-retry backoff for the reconnecting screen; after the last entry it goes manual-only. */
export const AUTO_RETRY_SCHEDULE_MS: readonly number[] = [3_000, 5_000, 10_000, 20_000, 30_000]

/** Delay before auto-retry number `attempt` (0-based), or null once the schedule is exhausted. */
export function nextAutoRetryDelayMs(attempt: number): number | null {
	return AUTO_RETRY_SCHEDULE_MS[attempt] ?? null
}

// ---------------------------------------------------------------------------
// Retry bookkeeping — MODULE-level, not component state: the router error boundary remounts
// the error component after every failed retry (`loadedAt` changes), so a useState counter
// would silently reset to the first delay forever, hammering a struggling backend.
// ---------------------------------------------------------------------------

/** Forget a retry record once it's this stale — a fresh failure gets a fresh backoff. */
export const RETRY_STATE_TTL_MS = 5 * 60 * 1000

interface RetryState {
	href: string
	firstFailedAt: number
	attempts: number
}

let retryState: RetryState | null = null

/** Attempts made so far for the CURRENT failure (same href, within TTL); 0 for a new failure. */
export function readRetryAttempts(href: string, now: number): number {
	if (!retryState || retryState.href !== href || now - retryState.firstFailedAt > RETRY_STATE_TTL_MS) {
		return 0
	}
	return retryState.attempts
}

/** Record one more attempt for this failure and return the new attempt count. */
export function bumpRetryAttempts(href: string, now: number): number {
	if (!retryState || retryState.href !== href || now - retryState.firstFailedAt > RETRY_STATE_TTL_MS) {
		retryState = { href, firstFailedAt: now, attempts: 1 }
	} else {
		retryState = { ...retryState, attempts: retryState.attempts + 1 }
	}
	return retryState.attempts
}

/** Clear the record (successful recovery, or between tests). */
export function resetRetryState(): void {
	retryState = null
}
